import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { alpha } from '@mui/material/styles';
import { API_BASE_URL, fetchJson, tryPaths } from './api';
import { AnalyticsMetricCard, AnalyticsPageHero, AnalyticsPanelSection, glassSubCardSx } from './analyticsSurface';

const FUNDERS_API_PATHS = ['/funders', '/api/funders', '/clients', '/api/clients'];

function getAuthHeader() {
    try {
        const token = localStorage.getItem('monsurv_auth_token') || '';
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function nonApproachKey(entityType, entityId) {
    return `${String(entityType || '').trim().toLowerCase()}:${String(entityId || '').trim().toLowerCase()}`;
}

function normalizeFunder(f) {
    if (!f || typeof f !== 'object') return null;
    const id = f.id ?? f.funder_id ?? f.client_id ?? f.company_name ?? f.companyName;
    const companyName = String(f.companyName ?? f.company_name ?? f.name ?? '').trim();
    if (!id && !companyName) return null;
    return {
        id: String(id || companyName).trim(),
        companyName,
        email: String(f.email || '').trim(),
        leadContact: String(f.leadContact ?? f.lead_contact ?? f.contact ?? '').trim(),
        phone: String(f.phone || '').trim()
    };
}

function normalizeProspects(query, json) {
    const list = Array.isArray(json?.prospects) ? json.prospects : (Array.isArray(json) ? json : []);
    const mapped = list
        .map((item, index) => {
            if (!item || typeof item !== 'object') return null;
            const companyName = String(item.company_name ?? item.companyName ?? item.name ?? '').trim();
            const email = String(item.email ?? '').trim();
            if (!companyName && !email) return null;
            return {
                id: String(item.id || item.external_id || companyName || `p-${index}`).trim(),
                companyName,
                email,
                website: String(item.website || '').trim(),
                region: String(item.region || item.location || '').trim(),
                source: String(item.source || 'Lookup API').trim()
            };
        })
        .filter(Boolean);

    if (mapped.length) return mapped;

    // Placeholder fallback until external lookup API is integrated.
    const q = String(query || '').trim();
    if (!q) return [];
    return [
        { id: `mock-${q}-1`, companyName: `${q} Developments Ltd`, email: `info@${q.toLowerCase().replace(/\s+/g, '')}.co.uk`, website: '', region: 'UK', source: 'Placeholder' },
        { id: `mock-${q}-2`, companyName: `${q} Property Group`, email: `projects@${q.toLowerCase().replace(/\s+/g, '')}group.co.uk`, website: '', region: 'UK', source: 'Placeholder' }
    ];
}

function buildEmailPrompt({ audienceType, tone, objective, companyName, context }) {
    return [
        'Draft a professional UK business development email for a monitoring survey business.',
        `Audience type: ${audienceType}.`,
        `Objective: ${objective}.`,
        `Tone: ${tone}.`,
        companyName ? `Recipient company: ${companyName}.` : '',
        context ? `Additional context: ${context}.` : '',
        'Return plain text with this exact structure:',
        'Subject: <single line>',
        'Body:',
        '<email body>'
    ].filter(Boolean).join('\n');
}

function parseEmailDraft(text) {
    const raw = String(text || '').trim();
    if (!raw) return { subject: '', body: '' };

    const subjectMatch = raw.match(/^subject\s*:\s*(.+)$/im);
    const bodyMatch = raw.match(/^body\s*:\s*([\s\S]*)$/im);
    const subject = subjectMatch ? String(subjectMatch[1] || '').trim() : '';
    const body = bodyMatch ? String(bodyMatch[1] || '').trim() : raw;

    return {
        subject: subject || 'Checking in from Monsurv',
        body
    };
}

async function fetchFundersFromApi() {
    const data = await tryPaths(
        FUNDERS_API_PATHS,
        (path) => `${API_BASE_URL}${path}`,
        { method: 'GET', headers: { ...getAuthHeader() } }
    );
    const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
    return list.map(normalizeFunder).filter(Boolean);
}

async function fetchFunderMetricsFromApi() {
    const data = await tryPaths(
        ['/funders/metrics', '/api/funders/metrics'],
        (path) => `${API_BASE_URL}${path}`,
        { method: 'GET', headers: { ...getAuthHeader() } }
    );
    const list = Array.isArray(data?.metrics) ? data.metrics : (Array.isArray(data) ? data : []);
    return list.map((m) => ({
        funderKey: String(m.funder_key || '').trim().toLowerCase(),
        activeProjects: Number(m.active_projects || 0)
    }));
}

export default function BusinessDevelopment() {
    const [tab, setTab] = useState('prospects');

    const [clients, setClients] = useState([]);
    const [metrics, setMetrics] = useState([]);
    const [loadingClients, setLoadingClients] = useState(false);
    const [clientsError, setClientsError] = useState('');

    const [searchQuery, setSearchQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [prospects, setProspects] = useState([]);

    const [nonApproach, setNonApproach] = useState({});
    const [nonApproachLoading, setNonApproachLoading] = useState(false);
    const [nonApproachError, setNonApproachError] = useState('');
    const [nonApproachBusyKey, setNonApproachBusyKey] = useState('');

    const [selectedRecipientType, setSelectedRecipientType] = useState('client');
    const [selectedRecipientId, setSelectedRecipientId] = useState('');
    const [tone, setTone] = useState('confident');
    const [objective, setObjective] = useState('introduce_services');
    const [context, setContext] = useState('');
    const [draftSubject, setDraftSubject] = useState('');
    const [draftBody, setDraftBody] = useState('');
    const [draftBusy, setDraftBusy] = useState(false);
    const [draftError, setDraftError] = useState('');
    const [sendBusy, setSendBusy] = useState(false);
    const [sendMessage, setSendMessage] = useState('');

    async function loadNonApproachMapFromApi() {
        setNonApproachLoading(true);
        setNonApproachError('');
        try {
            const json = await fetchJson(`${API_BASE_URL}/api/business-development/non-approach`, {
                method: 'GET',
                headers: { ...getAuthHeader() }
            });
            const map = json?.map && typeof json.map === 'object' ? json.map : {};
            setNonApproach(map);
        } catch (e) {
            setNonApproachError(String(e?.message || 'Failed to load Non Approach list'));
            setNonApproach({});
        } finally {
            setNonApproachLoading(false);
        }
    }

    useEffect(() => {
        loadNonApproachMapFromApi();
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingClients(true);
            setClientsError('');
            try {
                const [funderList, metricsList] = await Promise.all([
                    fetchFundersFromApi(),
                    fetchFunderMetricsFromApi()
                ]);
                if (cancelled) return;
                setClients(funderList);
                setMetrics(metricsList);
            } catch (e) {
                if (cancelled) return;
                setClients([]);
                setMetrics([]);
                setClientsError(String(e?.message || 'Failed to load client relationship data'));
            } finally {
                if (!cancelled) setLoadingClients(false);
            }
        })();

        return () => { cancelled = true; };
    }, []);

    const activeClientRows = useMemo(() => {
        const metricByKey = new Map((metrics || []).map((m) => [String(m?.funderKey || '').trim().toLowerCase(), m]));
        return (clients || []).map((client) => {
            const key = String(client?.companyName || '').trim().toLowerCase();
            const m = metricByKey.get(key) || null;
            const activeProjects = Number(m?.activeProjects || 0);
            const status = activeProjects > 0 ? 'Active' : 'Inactive';
            const mapKey = nonApproachKey('client', client?.id || key);
            return {
                ...client,
                activeProjects,
                status,
                nonApproach: Boolean(nonApproach?.[mapKey])
            };
        });
    }, [clients, metrics, nonApproach]);

    const recipientOptions = useMemo(() => {
        const clientOptions = activeClientRows.map((client) => ({
            id: String(client.id),
            type: 'client',
            label: client.companyName || client.id,
            email: client.email || '',
            nonApproach: Boolean(client.nonApproach)
        }));
        const prospectOptions = (prospects || []).map((prospect) => ({
            id: String(prospect.id),
            type: 'prospect',
            label: prospect.companyName || prospect.id,
            email: prospect.email || '',
            nonApproach: Boolean(nonApproach?.[nonApproachKey('prospect', prospect.id)])
        }));
        return selectedRecipientType === 'prospect' ? prospectOptions : clientOptions;
    }, [activeClientRows, nonApproach, prospects, selectedRecipientType]);

    const selectedRecipient = useMemo(() => (
        recipientOptions.find((item) => String(item.id) === String(selectedRecipientId)) || null
    ), [recipientOptions, selectedRecipientId]);

    const summaryMetrics = useMemo(() => {
        const totalClients = activeClientRows.length;
        const activeClients = activeClientRows.filter((row) => row.status === 'Active').length;
        const nonApproachClients = activeClientRows.filter((row) => row.nonApproach).length;
        const nonApproachProspects = (prospects || []).filter((p) => nonApproach?.[nonApproachKey('prospect', p.id)]).length;
        return {
            totalClients,
            activeClients,
            nonApproachTotal: nonApproachClients + nonApproachProspects,
            searchableProspects: (prospects || []).length
        };
    }, [activeClientRows, nonApproach, prospects]);

    async function searchProspects() {
        const q = String(searchQuery || '').trim();
        if (!q) return;
        setSearchLoading(true);
        setSearchError('');
        try {
            const endpoint = `${API_BASE_URL}/api/business-development/prospects/search?q=${encodeURIComponent(q)}`;
            const json = await fetchJson(endpoint, { method: 'GET', headers: { ...getAuthHeader() } });
            setProspects(normalizeProspects(q, json));
        } catch (e) {
            setProspects([]);
            setSearchError(String(e?.message || 'Prospect lookup failed'));
        } finally {
            setSearchLoading(false);
        }
    }

    async function toggleNonApproach(entityType, entityId) {
        const key = nonApproachKey(entityType, entityId);
        const nextValue = !Boolean(nonApproach?.[key]);
        setNonApproachBusyKey(key);
        try {
            await fetchJson(`${API_BASE_URL}/api/business-development/non-approach`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({
                    entity_type: entityType,
                    entity_id: entityId,
                    is_non_approach: nextValue
                })
            });
            setNonApproach((prev) => {
                const next = { ...(prev || {}) };
                if (nextValue) next[key] = true;
                else delete next[key];
                return next;
            });
        } catch (e) {
            setDraftError(String(e?.message || 'Failed to update Non Approach flag'));
        } finally {
            setNonApproachBusyKey('');
        }
    }

    async function generateDraft() {
        setDraftError('');
        setSendMessage('');
        if (!selectedRecipient) {
            setDraftError('Select a recipient before generating an email draft.');
            return;
        }
        if (selectedRecipient.nonApproach) {
            setDraftError('This recipient is marked as Non Approach. Remove that flag to generate outreach.');
            return;
        }

        setDraftBusy(true);
        try {
            const prompt = buildEmailPrompt({
                audienceType: selectedRecipientType,
                tone,
                objective,
                companyName: selectedRecipient.label,
                context
            });

            const json = await fetchJson(`${API_BASE_URL}/ai/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify({
                    messages: [{ role: 'user', content: prompt }],
                    context: { role: 'surveyor' }
                })
            });

            const text = String(json?.reply?.text || json?.text || '').trim();
            const parsed = parseEmailDraft(text);
            setDraftSubject(parsed.subject);
            setDraftBody(parsed.body);
        } catch (e) {
            setDraftError(String(e?.message || 'Failed to generate draft'));
        } finally {
            setDraftBusy(false);
        }
    }

    async function sendDraft() {
        setDraftError('');
        setSendMessage('');
        if (!selectedRecipient) {
            setDraftError('Select a recipient before sending.');
            return;
        }
        if (!String(selectedRecipient.email || '').trim()) {
            setDraftError('Selected recipient does not have an email address.');
            return;
        }
        if (!String(draftSubject || '').trim() || !String(draftBody || '').trim()) {
            setDraftError('Generate or edit a draft before sending.');
            return;
        }
        if (selectedRecipient.nonApproach) {
            setDraftError('This recipient is marked as Non Approach and cannot be emailed.');
            return;
        }

        setSendBusy(true);
        try {
            const payload = {
                recipient_type: selectedRecipient.type,
                recipient_id: selectedRecipient.id,
                to: selectedRecipient.email,
                subject: draftSubject,
                body: draftBody,
                meta: { tone, objective }
            };

            const result = await fetchJson(`${API_BASE_URL}/api/business-development/outreach/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
                body: JSON.stringify(payload)
            });
            const sent = Boolean(result?.sent);
            const reason = String(result?.reason || '').trim();
            setSendMessage(sent ? 'Email sent successfully.' : (reason || 'Email was queued/logged but not sent.'));
        } catch (e) {
            setSendMessage(String(e?.message || 'Failed to send outreach email'));
        } finally {
            setSendBusy(false);
        }
    }

    return (
        <Box sx={{ display: 'grid', gap: 2.25 }}>
            <AnalyticsPageHero
                eyebrow="Management Workspace"
                title="Business development"
                chips={['Prospects', 'Relationship health', 'Approved outreach']}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', xl: 'repeat(4, minmax(0,1fr))' }, gap: 2 }}>
                <AnalyticsMetricCard
                    icon={<GroupsRoundedIcon fontSize="small" />}
                    label="Clients"
                    value={summaryMetrics.totalClients}
                    caption="Current client organisations in your register."
                    accent="linear-gradient(135deg, #1d4ed8, #0f172a)"
                />
                <AnalyticsMetricCard
                    icon={<CampaignRoundedIcon fontSize="small" />}
                    label="Active Clients"
                    value={summaryMetrics.activeClients}
                    caption="Clients with active projects based on current metrics."
                    accent="linear-gradient(135deg, #047857, #0f766e)"
                />
                <AnalyticsMetricCard
                    icon={<SearchRoundedIcon fontSize="small" />}
                    label="Prospects"
                    value={summaryMetrics.searchableProspects}
                    caption="Prospects loaded in current discovery session."
                    accent="linear-gradient(135deg, #7c3aed, #4338ca)"
                />
                <AnalyticsMetricCard
                    icon={<MarkEmailReadRoundedIcon fontSize="small" />}
                    label="Non Approach"
                    value={summaryMetrics.nonApproachTotal}
                    caption="Recipients currently protected from outreach."
                    accent="linear-gradient(135deg, #b45309, #92400e)"
                />
            </Box>

            <Paper sx={{ borderRadius: 3, border: `1px solid ${alpha('#0f172a', 0.08)}`, overflow: 'hidden' }}>
                <Tabs value={tab} onChange={(_, next) => setTab(next)}>
                    <Tab value="prospects" label="Prospect Discovery" />
                    <Tab value="relationships" label="Relationship Health" />
                    <Tab value="outreach" label="Outreach Studio" />
                </Tabs>
            </Paper>

            {tab === 'prospects' ? (
                <AnalyticsPanelSection
                    eyebrow="Prospecting"
                    title="Search for new client opportunities"
                >
                    <Stack spacing={1.5}>
                        {nonApproachLoading ? <Alert severity="info">Loading Non Approach recipients...</Alert> : null}
                        {nonApproachError ? <Alert severity="warning">{nonApproachError}</Alert> : null}
                        {searchError ? <Alert severity="info">{searchError}</Alert> : null}
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <TextField
                                fullWidth
                                label="Search term"
                                placeholder="e.g. Liverpool student accommodation developer"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Button variant="contained" onClick={searchProspects} disabled={searchLoading || !String(searchQuery).trim()}>
                                {searchLoading ? 'Searching...' : 'Search'}
                            </Button>
                        </Stack>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
                            {(prospects || []).map((prospect) => {
                                const key = nonApproachKey('prospect', prospect.id);
                                const flagged = Boolean(nonApproach?.[key]);
                                return (
                                    <Paper key={prospect.id} sx={{ ...glassSubCardSx, p: 1.4 }}>
                                        <Stack spacing={0.8}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                                <Typography sx={{ fontWeight: 900 }}>{prospect.companyName || 'Unnamed prospect'}</Typography>
                                                <Chip size="small" label={prospect.source || 'Lookup'} />
                                            </Stack>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{prospect.email || 'No email available'}</Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{prospect.region || 'Region unknown'}</Typography>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <Switch
                                                    checked={flagged}
                                                    disabled={nonApproachBusyKey === key}
                                                    onChange={() => toggleNonApproach('prospect', prospect.id)}
                                                />
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>Non Approach</Typography>
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Stack>
                </AnalyticsPanelSection>
            ) : null}

            {tab === 'relationships' ? (
                <AnalyticsPanelSection
                    eyebrow="Client Health"
                    title="Assess relationship coverage"
                    subtitle="Active status is based on active projects and can be manually controlled using the Non Approach flag."
                >
                    <Stack spacing={1.2}>
                        {loadingClients ? <Alert severity="info">Loading client relationship data...</Alert> : null}
                        {clientsError ? <Alert severity="error">{clientsError}</Alert> : null}

                        {(activeClientRows || []).map((client) => {
                            const tagColor = client.status === 'Active' ? 'success' : 'default';
                            return (
                                <Paper key={client.id} sx={{ ...glassSubCardSx, p: 1.4 }}>
                                    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                                        <Stack spacing={0.5}>
                                            <Typography sx={{ fontWeight: 900 }}>{client.companyName || client.id}</Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{client.email || 'No primary email'}</Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Lead: {client.leadContact || 'Not set'}</Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Chip color={tagColor} label={`${client.status} (${client.activeProjects} projects)`} />
                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                <Switch
                                                    checked={Boolean(client.nonApproach)}
                                                    disabled={nonApproachBusyKey === nonApproachKey('client', client.id)}
                                                    onChange={() => toggleNonApproach('client', client.id)}
                                                />
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>Non Approach</Typography>
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Paper>
                            );
                        })}
                    </Stack>
                </AnalyticsPanelSection>
            ) : null}

            {tab === 'outreach' ? (
                <AnalyticsPanelSection
                    eyebrow="Outreach"
                    title="Generate and approve outreach emails"
                    subtitle="Create AI drafts from strategic prompts, then send via SMTP only after explicit approval."
                >
                    <Stack spacing={1.4}>
                        {draftError ? <Alert severity="error">{draftError}</Alert> : null}
                        {sendMessage ? <Alert severity="info">{sendMessage}</Alert> : null}

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <FormControl sx={{ minWidth: 180 }}>
                                <InputLabel>Recipient Type</InputLabel>
                                <Select
                                    label="Recipient Type"
                                    value={selectedRecipientType}
                                    onChange={(e) => {
                                        setSelectedRecipientType(e.target.value);
                                        setSelectedRecipientId('');
                                    }}
                                >
                                    <MenuItem value="client">Existing Client</MenuItem>
                                    <MenuItem value="prospect">Prospect</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl sx={{ minWidth: 280, flex: 1 }}>
                                <InputLabel>Recipient</InputLabel>
                                <Select label="Recipient" value={selectedRecipientId} onChange={(e) => setSelectedRecipientId(e.target.value)}>
                                    {recipientOptions.map((option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.label}{option.nonApproach ? ' (Non Approach)' : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
                            <FormControl sx={{ minWidth: 220 }}>
                                <InputLabel>Objective</InputLabel>
                                <Select label="Objective" value={objective} onChange={(e) => setObjective(e.target.value)}>
                                    <MenuItem value="introduce_services">Introduce services</MenuItem>
                                    <MenuItem value="reengage_existing">Re-engage existing relationship</MenuItem>
                                    <MenuItem value="new_project_follow_up">Follow up on likely project pipeline</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 180 }}>
                                <InputLabel>Tone</InputLabel>
                                <Select label="Tone" value={tone} onChange={(e) => setTone(e.target.value)}>
                                    <MenuItem value="confident">Confident</MenuItem>
                                    <MenuItem value="consultative">Consultative</MenuItem>
                                    <MenuItem value="direct">Direct</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        <TextField
                            multiline
                            minRows={2}
                            label="Context notes"
                            value={context}
                            onChange={(e) => setContext(e.target.value)}
                            placeholder="Optional detail for the AI draft, e.g. current market focus or prior discussions"
                        />

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Button variant="contained" onClick={generateDraft} disabled={draftBusy || sendBusy}>
                                {draftBusy ? 'Generating...' : 'Generate Draft'}
                            </Button>
                            <Button variant="outlined" onClick={sendDraft} disabled={draftBusy || sendBusy}>
                                {sendBusy ? 'Sending...' : 'Approve and Send'}
                            </Button>
                        </Stack>

                        <TextField
                            label="Subject"
                            value={draftSubject}
                            onChange={(e) => setDraftSubject(e.target.value)}
                            placeholder="Generated subject appears here"
                        />
                        <TextField
                            multiline
                            minRows={8}
                            label="Body"
                            value={draftBody}
                            onChange={(e) => setDraftBody(e.target.value)}
                            placeholder="Generated email body appears here"
                        />
                    </Stack>
                </AnalyticsPanelSection>
            ) : null}
        </Box>
    );
}
